import type { FCWithChildren } from '@pbk-web/types';
import { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { v4 as uuid } from 'uuid';

import { ProcessesCallbacksNames, ProcessesMethodNames } from '../../hubs/ProcessesHub';
import { usePbkAuth } from '../PbkAuthContext';
import { useSignalRHub } from './useSignalRHub';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Obj = Record<string, any>;

type Actions = {
  listenToProcess: <TSuccess extends Obj>(listenToId: string, timeoutSeconds?: number) => Promise<TSuccess>;
};

export const SignalRProcessesContext = createContext<Actions>(null!);

export const useProcessesHub = (): Actions => useContext(SignalRProcessesContext);

export const SignalRProcessesProvider: FCWithChildren = (props) => {
  const { children } = props;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const listeners = useRef<Record<string, { timeoutId: string; resolve: (value: any) => void; reject: () => void }[]>>({});

  const { isAuthenticated } = usePbkAuth();

  const { invoke, signalRConnection, useSignalREffect } = useSignalRHub({
    url: `${import.meta.env.VITE_API_BASE_URL}/hubs/processes`,
    dependencies: [],
    disable: !isAuthenticated,
  });

  const listenToProcess = useCallback(
    <TSuccess extends Obj>(listenToId: string, timeoutSeconds = -1) => {
      const promise = new Promise<TSuccess>((resolve, reject) => {
        const timeoutId = uuid();

        if (!listeners.current[listenToId]) listeners.current[listenToId] = [];
        listeners.current[listenToId].push({ timeoutId, resolve, reject });

        if (timeoutSeconds > 0) {
          setTimeout(() => {
            listeners.current[listenToId] = listeners.current[listenToId].filter((x) => x.timeoutId !== timeoutId);

            invoke(ProcessesMethodNames.UnsubscribeFromProcess, listenToId);
            reject('timeout waiting for process ' + listenToId);
          }, timeoutSeconds * 1000);
        }
      });

      invoke(ProcessesMethodNames.SubscribeToProcess, listenToId);

      return promise;
    },
    [invoke],
  );

  useSignalREffect(
    ProcessesCallbacksNames.Success,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (id: string, data: any) => {
      if (!listeners.current[id]?.length) {
        return;
      }

      invoke(ProcessesMethodNames.UnsubscribeFromProcess, id);
      const toResolve = listeners.current[id];
      listeners.current[id] = [];
      for (const listener of toResolve) {
        listener.resolve(data);
      }
    },
    [],
  );

  useSignalREffect(
    ProcessesCallbacksNames.Failure,
    (id: string) => {
      if (!listeners.current[id]?.length) {
        return;
      }

      invoke(ProcessesMethodNames.UnsubscribeFromProcess, id);
      const toResolve = listeners.current[id];
      listeners.current[id] = [];
      for (const listener of toResolve) {
        listener.reject();
      }
    },
    [],
  );

  const contextValues = useMemo(
    () => ({
      listenToProcess,
    }),
    [listenToProcess],
  );

  return (
    <SignalRProcessesContext.Provider value={contextValues}>
      {isAuthenticated && signalRConnection}
      {children}
    </SignalRProcessesContext.Provider>
  );
};
